const JOB_OPTIONS = [
	{ label: 'Уборщик', value: 1 },
	{ label: 'Директор', value: 2 },
	{ label: 'Менеджер', value: 3 },
];

const NUMBER_KIND_OPTIONS = {
	WORK: { label: 'Рабочий', value: 'work' },
	PERSONAL: { label: 'Личный', value: 'personal' },
};

export const findJobOption = (position) => {
	if (!position) return null;

	return (
		JOB_OPTIONS.find((job) => job.label === position) || {
			label: position,
			value: position,
		}
	);
};

const mapContactStatus = (active) => ({
	label: active !== false ? 'Работает' : 'Больше не работает',
	value: active !== false ? 'avail' : 'unavail',
});

const mapNumbersToFormContacts = (numbers = []) =>
	numbers
		.filter((number) => !number.isDeleted)
		.map((number) => ({
			type: NUMBER_KIND_OPTIONS[number.kind] || NUMBER_KIND_OPTIONS.WORK,
			text: number.number,
		}));

const normalizeText = (value) => {
	if (value === null || value === undefined) return '';

	return String(value).trim();
};

const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

const formatFormPhones = (contacts = []) =>
	(contacts || [])
		.filter(({ text }) => normalizePhone(text))
		.map(({ text, type }) => `${text} (${type?.label || '—'})`)
		.join('\n');

const getPhoneEntries = (contacts = []) =>
	(contacts || [])
		.filter(({ text }) => normalizePhone(text))
		.map(({ text, type }) => ({
			type: type?.value || 'work',
			number: normalizePhone(text),
		}))
		.sort((a, b) =>
			`${a.type}:${a.number}`.localeCompare(`${b.type}:${b.number}`),
		);

const arePhoneListsEqual = (left = [], right = []) => {
	const a = getPhoneEntries(left);
	const b = getPhoneEntries(right);

	if (a.length !== b.length) return false;

	return a.every(
		(entry, index) =>
			entry.type === b[index].type && entry.number === b[index].number,
	);
};

const pushTextChange = (options, { id, label, next, prev }) => {
	const value = normalizeText(next);
	const old = normalizeText(prev);

	if (value === old) return;

	options.push({
		id,
		label,
		value,
		...(old ? { old } : {}),
	});
};

export const eventFormContactsMapper = (personal, general) => {
	return [
		...personal.map((contact) => {
			const { firstName, lastName, middleName, comment } = contact;

			return {
				label: `${lastName} ${firstName}${middleName ? ` ${middleName}` : ''}`,
				remark: comment,
				value: String(contact.id),
				isPerson: true,
				...contact,
			};
		}),
		...general.map(({ id, number, comment, active }) => ({
			id,
			value: String(id),
			label: number,
			number,
			remark: comment,
			comment,
			active,
			belowLine: true,
			isPerson: false,
		})),
	];
};

export const mapContactOptionToChangeFormValues = (option) => {
	if (!option) {
		return {
			target: null,
			status: null,
			phone: '',
			name: '',
			secondName: '',
			thirdName: '',
			contacts: [{ type: null, text: '' }],
			job: null,
			decisionMaker: '1',
			comment: '',
		};
	}

	const target = { ...option, value: option.value ?? String(option.id) };
	const status = mapContactStatus(option.active);

	if (option.isPerson) {
		const numbers = mapNumbersToFormContacts(option.numbers);

		return {
			target,
			status,
			name: option.firstName || '',
			secondName: option.lastName || '',
			thirdName: option.middleName || '',
			contacts: numbers.length ? numbers : [{ type: null, text: '' }],
			job: findJobOption(option.position),
			decisionMaker: option.responsiblePerson ? '1' : '0',
			comment: option.comment || '',
		};
	}

	return {
		target: { ...target, isPerson: false },
		status,
		phone: option.number || option.label || '',
		comment: option.comment || option.remark || '',
	};
};

const mapPersonalContactChangePreview = (data, initial) => {
	const options = [];

	pushTextChange(options, {
		id: 1,
		label: 'Фамилия',
		next: data.secondName,
		prev: initial.secondName,
	});

	pushTextChange(options, {
		id: 2,
		label: 'Имя',
		next: data.name,
		prev: initial.name,
	});

	pushTextChange(options, {
		id: 3,
		label: 'Отчество',
		next: data.thirdName,
		prev: initial.thirdName,
	});

	if (!arePhoneListsEqual(data.contacts, initial.contacts)) {
		options.push({
			id: 4,
			label: 'Телефон',
			value: formatFormPhones(data.contacts),
			...(formatFormPhones(initial.contacts)
				? { old: formatFormPhones(initial.contacts) }
				: {}),
		});
	}

	pushTextChange(options, {
		id: 5,
		label: 'Должность',
		next: data.job?.label,
		prev: initial.job?.label,
	});

	const newDecisionMaker = data.decisionMaker === '1' ? 'Да' : 'Нет';
	const oldDecisionMaker = initial.decisionMaker === '1' ? 'Да' : 'Нет';

	if (newDecisionMaker !== oldDecisionMaker) {
		options.push({
			id: 6,
			label: 'Лицо, принимающее решения',
			value: newDecisionMaker,
			old: oldDecisionMaker,
		});
	}

	if (data.status?.value !== initial.status?.value) {
		options.push({
			id: 7,
			label: 'Статус',
			value: data.status?.label || '',
			old: initial.status?.label || '',
		});
	}

	pushTextChange(options, {
		id: 8,
		label: 'Комментарий',
		next: data.comment,
		prev: initial.comment,
	});

	if (!options.length) return null;

	return {
		groupIndex: 1,
		title: `${data.secondName} ${data.name} ${data.thirdName}`.trim(),
		typoData: { options },
	};
};

const mapGeneralNumberChangePreview = (data, initial) => {
	const options = [];
	const newPhone = normalizePhone(data.phone);
	const oldPhone = normalizePhone(initial.phone);

	if (newPhone !== oldPhone) {
		options.push({
			id: 1,
			label: 'Телефон',
			value: data.phone || '',
			...(initial.phone ? { old: initial.phone } : {}),
		});
	}

	pushTextChange(options, {
		id: 2,
		label: 'Комментарий',
		next: data.comment,
		prev: initial.comment,
	});

	if (data.status?.value !== initial.status?.value) {
		options.push({
			id: 3,
			label: 'Статус',
			value: data.status?.label || '',
			old: initial.status?.label || '',
		});
	}

	if (!options.length) return null;

	return {
		groupIndex: 1,
		title: data.phone || initial.phone,
		typoData: { options },
	};
};

export const mapContactChangePreview = (data) => {
	const original = data.target;

	if (!original) return null;

	const initial = mapContactOptionToChangeFormValues(original);

	if (original.isPerson) {
		return mapPersonalContactChangePreview(data, initial);
	}

	return mapGeneralNumberChangePreview(data, initial);
};
